/**
 * One flat shape for an opportunity row, plus the small presentational maps
 * `OpportunitiesPage`/`PipelinePage` share.
 */

/** md.opportunity_stage.forecast_category CHECK → pill tone. Real stages are
 * tenant-configurable, so tone follows the category, not a fixed per-stage list
 * like the prototype's index-based `stagePill` array. */
export const FORECAST_TONE = {
  Pipeline: 'gray',
  'Best Case': 'sky',
  Commit: 'violet',
  'Closed Won': 'green',
  'Closed Lost': 'rose',
};

export function forecastTone(category) {
  return FORECAST_TONE[category] ?? 'gray';
}

const HEAT_BY_DAYS = (days) => (days > 45 ? ['pill-rose', 'Hot'] : days > 20 ? ['pill-amber', 'Warm'] : ['pill-sky', 'Cold']);
export function ageHeat(days) {
  return HEAT_BY_DAYS(days ?? 0);
}

/**
 * An API opportunity as the flat row the views render.
 * @param {object} o
 */
export function toRow(o) {
  return {
    id: o.id,
    opportunityNo: o.opportunity_no,
    name: o.name,
    account: o.account,
    primaryContact: o.primary_contact,
    pipeline: o.pipeline,
    stage: o.stage,
    amount: o.amount,
    currency: o.currency,
    amountBase: o.amount_base,
    probability: o.probability,
    probabilityIsOverride: o.probability_is_override,
    weightedAmount: o.weighted_amount,
    expectedCloseDate: o.expected_close_date,
    originalCloseDate: o.original_close_date,
    slippedDays: o.slipped_days,
    ageDays: o.age_days,
    daysInStage: o.days_in_stage,
    nextStep: o.next_step,
    owner: o.owner,
    isClosed: o.is_closed,
    isWon: o.is_won,
    lostReason: o.lost_reason,
    lostNote: o.lost_note,
    lostToCompetitor: o.lost_to_competitor,
    competitors: o.competitors ?? [],
    lastActivityAt: o.last_activity_at,
    createdAt: o.created_at,
    updatedAt: o.updated_at,
    rowVersion: o.row_version,
  };
}

/** The prototype's tab strip, each mapped onto real `GET /opportunities` query params. */
export const OPEN_TABS = [
  { key: 'open', label: 'Open', params: { is_closed: 'false' } },
  { key: 'closing', label: 'Closing this month', params: { is_closed: 'false' } },
  { key: 'stalled', label: 'Stalled', params: { is_closed: 'false', stale_days_gte: 30 } },
  { key: 'won', label: 'Won', params: { is_closed: 'true', is_won: 'true' } },
  { key: 'lost', label: 'Lost', params: { is_closed: 'true', is_won: 'false' } },
];

/** "Closing this month" needs the actual month boundaries, computed at render time, not a fixed param. */
export function closingThisMonthRange() {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
  const iso = (d) => d.toISOString().slice(0, 10);
  return { close_date_from: iso(from), close_date_to: iso(to) };
}
